/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Decimal } from "decimal.js";

// Validates if a string is a valid currency amount with up to 2 decimal places (e.g. "123.45", ".50")
export const isValidCurrencyAmount = (value: string): boolean =>
  /^(?:[1-9][0-9]*|0)(?:\.[0-9]{1,2})?$|^\.[0-9]{1,2}$/.test(value);

// Validates price input including partial entries during typing (e.g. "", ".", "123.")
export const isValidPriceInput = (value: string): boolean => {
  if (value === "" || value === "." || /^(?:[1-9][0-9]*|0)\.$/.test(value)) {
    return true;
  }

  return isValidCurrencyAmount(value);
};

// Formats valid price input strings to 2 decimal places
export const formatPriceInput = (value: string): string | undefined => {
  if (value && /^(?:[1-9][0-9]*|0)(?:\.[0-9]*)?$|^\.[0-9]+$/.test(value)) {
    const numValue: number = parseFloat(value);
    if (!isNaN(numValue)) {
      return getFormattedUnitPrice(numValue);
    }
  }

  return undefined;
};

// Rounds monetary values to 2 decimal places using standard rounding (1.235 -> 1.24)
export const getFormattedUnitPrice = (price?: number): string | undefined => {
  if (typeof price !== "number" || !Number.isFinite(price)) {
    return undefined;
  }

  return price.toFixed(2);
};

// Truncates monetary values to 2 decimal places (1.235 -> 1.23) to match Farmlands PRO API calculations
export const truncateToTwoDecimals = (value: number): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value) && !Number.isNaN(value)) {
    return new Decimal(value).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();
  }

  return undefined;
};

// Checks if two prices are different by truncating them to 2 decimal places
export const isPriceDifferent = (priceA: unknown, priceB: unknown): boolean | undefined => {
  const truncatedPriceA: number | undefined = truncateToTwoDecimals(priceA as number);
  const truncatedPriceB: number | undefined = truncateToTwoDecimals(priceB as number);

  if (truncatedPriceA === undefined || truncatedPriceB === undefined) {
    return undefined;
  }

  return truncatedPriceA !== truncatedPriceB;
};

export const calculateDiscountPrice = (
  originalPrice: number | undefined,
  discountPercentage: number
) => {
  if (!originalPrice) {
    return 0;
  }

  const sanitizedDiscountPercentage = Math.min(100, Math.max(discountPercentage, 0));
  const percentage = sanitizedDiscountPercentage / 100;
  const maximumDiscountPrice = originalPrice * (1 - percentage);
  // Only format to 2 decimal places if there are more than 2 digits after the decimal point
  const decimalPart = maximumDiscountPrice.toString().split(".")[1];
  if (decimalPart && decimalPart.length > 2) {
    return parseFloat(getFormattedUnitPrice(maximumDiscountPrice) as string);
  }
  return maximumDiscountPrice;
};
